import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { MapPin, CheckCircle, AlertCircle, Loader2, Truck } from "lucide-react";
import { API_URL } from "../config/api";


export default function DriverTrackingPage() {
  const { orderId, token } = useParams();
  const [status, setStatus] = useState("idle"); // idle, requesting, success, error
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const requestLocation = () => {
    setStatus("requesting");
    setError(null);

    if (!navigator.geolocation) {
      setStatus("error");
      setError("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setLocation({ latitude, longitude, accuracy });
        
        // Submit to server
        setSubmitting(true);
        try {
          const response = await fetch(
            `${API_URL}/track/${orderId}/${token}/submit?latitude=${latitude}&longitude=${longitude}&accuracy=${accuracy}`,
            { method: "POST" }
          );
          
          if (response.ok) {
            const data = await response.json();
            setStatus("success");
            setLocation(prev => ({ ...prev, address: data.location }));
          } else {
            const err = await response.json();
            setStatus("error");
            setError(err.detail || "Failed to submit location");
          }
        } catch (e) {
          setStatus("error");
          setError("Network error. Please try again.");
        } finally {
          setSubmitting(false);
        }
      },
      (err) => {
        setStatus("error");
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError("Location access was denied. Please allow location access and try again.");
            break;
          case err.POSITION_UNAVAILABLE:
            setError("Location information is unavailable.");
            break;
          case err.TIMEOUT:
            setError("Location request timed out. Please try again.");
            break;
          default:
            setError("An unknown error occurred.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0
      }
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <Truck className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Europacific Tracking</h1>
          <p className="text-emerald-100 text-sm mt-1">
            Share your location for shipment tracking
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {status === "idle" && (
            <div className="text-center space-y-6">
              <div className="p-4 bg-blue-50 rounded-xl">
                <MapPin className="h-12 w-12 text-blue-500 mx-auto mb-3" />
                <p className="text-slate-600">
                  Tap the button below to share your current location with Europacific.
                </p>
                <p className="text-slate-500 text-sm mt-2">
                  Your browser will ask for permission to access your location.
                </p>
              </div>
              
              <Button 
                onClick={requestLocation}
                size="lg"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                data-testid="share-location-btn"
              >
                <MapPin className="h-5 w-5 mr-2" />
                Share My Location
              </Button>

              <p className="text-xs text-slate-400">
                Your location data is only used for shipment tracking and will not be stored permanently.
              </p>
            </div>
          )}

          {status === "requesting" && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 text-emerald-500 mx-auto animate-spin mb-4" />
              <p className="text-slate-600 font-medium">
                {submitting ? "Sending location..." : "Getting your location..."}
              </p>
              <p className="text-slate-400 text-sm mt-2">
                Please wait a moment
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center py-8 space-y-4">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-12 w-12 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Location Shared!</h2>
              <p className="text-slate-600">
                Thank you! Your location has been received.
              </p>
              {location?.address && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-sm text-slate-500">Your location:</p>
                  <p className="text-slate-700 font-medium">{location.address}</p>
                </div>
              )}
              <p className="text-sm text-emerald-600">
                You can close this page now.
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="text-center py-8 space-y-4">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="h-12 w-12 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Unable to Share Location</h2>
              <p className="text-red-600">{error}</p>
              
              <Button 
                onClick={requestLocation}
                size="lg"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="retry-location-btn"
              >
                Try Again
              </Button>

              <div className="text-left p-4 bg-slate-50 rounded-lg mt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Troubleshooting:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Make sure location services are enabled on your device</li>
                  <li>• Allow location access when your browser asks</li>
                  <li>• Try refreshing the page</li>
                  <li>• Make sure you have internet connection</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 text-center">
          <p className="text-xs text-slate-400">
            Europacific Logistika d.o.o. • Container Tracking System
          </p>
        </div>
      </div>
    </div>
  );
}
